import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import EntityScreeningResultsTable from 'components/Screening/tables/entityTable/EntityScreeningResultsTable';

export type ResultSource = 'all' | 'ownership';

interface BiteDBEntityScreeningResultsProps {
  allMatchesCount: number;
  ownershipResponse?: any;
  ownershipLoading: boolean;
  ownershipPage: number;
  setOwnershipPage: React.Dispatch<React.SetStateAction<number>>;
  ownershipSnapshot?: any;
  ownershipEnabled?: boolean;
  ownershipSettingLoading?: boolean;
  onOpenSettings?: () => void;
  entityName: string;
  renderAllMatches: (
    searchQuery: string,
    hideTableToolbar: boolean,
    onResultCountChange: (count: number) => void,
    actionsPortalTarget: HTMLElement | null,
  ) => React.ReactNode;
  onAllSearchChange: () => void;
  toolbarTrailingActions?: React.ReactNode;
  activeSource?: ResultSource;
  onSourceChange?: (source: ResultSource) => void;
}

export const OWNERSHIP_RESULTS_PAGE_SIZE = 100;

const BiteDBEntityScreeningResults: React.FC<
  BiteDBEntityScreeningResultsProps
> = ({
  allMatchesCount,
  ownershipResponse,
  ownershipLoading,
  ownershipPage,
  setOwnershipPage,
  ownershipSnapshot,
  ownershipEnabled = true,
  ownershipSettingLoading = false,
  onOpenSettings,
  entityName,
  renderAllMatches,
  onAllSearchChange,
  toolbarTrailingActions,
  activeSource: controlledActiveSource,
  onSourceChange,
}) => {
  const [internalActiveSource, setInternalActiveSource] =
    useState<ResultSource>('all');
  const activeSource = controlledActiveSource ?? internalActiveSource;
  const [actionsPortalTarget, setActionsPortalTarget] =
    useState<HTMLDivElement | null>(null);
  const [searchQueries, setSearchQueries] = useState<
    Record<ResultSource, string>
  >({
    all: '',
    ownership: '',
  });
  const ownershipApiPayload = ownershipResponse?.success
    ? ownershipResponse.result
    : null;
  const ownershipSnapshotPayload = Array.isArray(ownershipSnapshot?.matches)
    ? {
        total_hits: ownershipSnapshot.matches.length,
        source_total: ownershipSnapshot.matches.length,
        results: ownershipSnapshot.matches,
      }
    : null;
  // Prefer the bounded API page whenever it is available. The stored snapshot
  // remains a compatibility fallback for older records without paged results.
  const ownershipPayload = ownershipApiPayload ?? ownershipSnapshotPayload;
  const isServerPaginated = Boolean(ownershipApiPayload);
  const ownershipTotal = Number(ownershipPayload?.total_hits ?? 0);
  const [allTableCount, setAllTableCount] = useState<{
    total: number;
    count: number;
  } | null>(null);
  const [ownershipTableCount, setOwnershipTableCount] = useState<{
    total: number;
    count: number;
  } | null>(null);
  const reportAllCount = useCallback(
    (count: number) =>
      setAllTableCount({ total: allMatchesCount, count: count }),
    [allMatchesCount],
  );
  const reportOwnershipCount = useCallback(
    (count: number) =>
      setOwnershipTableCount({ total: ownershipTotal, count: count }),
    [ownershipTotal],
  );
  const displayedAllCount =
    allTableCount?.total === allMatchesCount
      ? allTableCount.count
      : allMatchesCount;
  const displayedOwnershipCount =
    ownershipTableCount?.total === ownershipTotal
      ? ownershipTableCount.count
      : ownershipTotal;
  const ownershipSelectable = ownershipEnabled && !ownershipSettingLoading;
  const effectiveSource = ownershipSelectable ? activeSource : 'all';
  const activeSearchQuery = searchQueries[effectiveSource];
  const ownershipControlsArePageScoped =
    effectiveSource === 'ownership' && isServerPaginated;

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(ownershipTotal / OWNERSHIP_RESULTS_PAGE_SIZE) - 1,
    );
    if (isServerPaginated && !ownershipLoading && ownershipPage > lastPage) {
      setOwnershipPage(0);
    }
  }, [
    isServerPaginated,
    ownershipLoading,
    ownershipPage,
    ownershipTotal,
    setOwnershipPage,
  ]);

  useEffect(() => {
    if (!ownershipSelectable && activeSource === 'ownership') {
      setInternalActiveSource('all');
      onSourceChange?.('all');
      setOwnershipPage(0);
    }
  }, [activeSource, onSourceChange, ownershipSelectable, setOwnershipPage]);

  const ownershipResults = useMemo(() => {
    const docs = Array.isArray(ownershipPayload?.results)
      ? ownershipPayload.results
      : [];
    const hits = docs.map((doc: any, index: number) => ({
      _id: String(doc?.record_id ?? doc?.['BITE Record ID'] ?? index),
      _source: doc,
    }));

    return {
      isLoading: ownershipLoading,
      data: [
        {
          result: {
            body: {
              hits: {
                total: { value: ownershipTotal },
                hits: hits,
              },
            },
          },
        },
      ],
    };
  }, [ownershipLoading, ownershipPayload, ownershipTotal]);

  const emptyEntityResults = useMemo(
    () => ({
      data: [
        {
          result: {
            body: {
              hits: {
                total: { value: 0 },
                hits: [],
              },
            },
          },
        },
      ],
    }),
    [],
  );

  const switchSource = (source: ResultSource) => {
    if (
      source === activeSource ||
      (source === 'ownership' && !ownershipSelectable)
    ) {
      return;
    }
    setInternalActiveSource(source);
    onSourceChange?.(source);
    setOwnershipPage(0);
  };

  return (
    <div className='entity-screening-results-shell'>
      <div className='entity-results-toolbar'>
        <div className='entity-results-search'>
          <SearchIcon sx={{ fontSize: 19 }} />
          <input
            value={activeSearchQuery}
            onChange={(event) => {
              const value = event.target.value;
              setSearchQueries((previous) => ({
                ...previous,
                [effectiveSource]: value,
              }));
              if (effectiveSource === 'all') {
                onAllSearchChange();
              }
              setOwnershipPage(0);
            }}
            placeholder={
              ownershipControlsArePageScoped
                ? 'Search current ownership page...'
                : 'Search entities, source, lens...'
            }
            aria-describedby={
              ownershipControlsArePageScoped
                ? 'ownership-results-page-scope'
                : undefined
            }
          />
          {activeSearchQuery && (
            <button
              type='button'
              className='entity-results-search-clear'
              aria-label='Clear screening results search'
              onClick={() => {
                setSearchQueries((previous) => ({
                  ...previous,
                  [effectiveSource]: '',
                }));
                if (effectiveSource === 'all') {
                  onAllSearchChange();
                }
                setOwnershipPage(0);
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>

        <div className='entity-results-tabs'>
          <button
            type='button'
            className={effectiveSource === 'all' ? 'active' : ''}
            onClick={() => switchSource('all')}
          >
            All Matches
            <span>{displayedAllCount.toLocaleString()}</span>
          </button>
          <button
            type='button'
            className={effectiveSource === 'ownership' ? 'active' : ''}
            onClick={() => switchSource('ownership')}
            disabled={!ownershipSelectable}
            aria-describedby={
              ownershipSelectable
                ? undefined
                : ownershipSettingLoading
                ? 'ownership-results-loading-copy'
                : 'ownership-results-disabled-copy'
            }
            title={
              ownershipSelectable
                ? undefined
                : ownershipSettingLoading
                ? 'Checking the Beneficial Ownership setting'
                : 'Beneficial Ownership is disabled in Database Settings'
            }
          >
            Ownership
            {ownershipSelectable ? (
              <span>{displayedOwnershipCount.toLocaleString()}</span>
            ) : (
              <span className='entity-results-tab-disabled-indicator'>
                {ownershipSettingLoading ? (
                  'Checking'
                ) : (
                  <>
                    <LockOutlinedIcon
                      aria-hidden='true'
                      sx={{ fontSize: 13 }}
                    />
                    Off
                  </>
                )}
              </span>
            )}
          </button>
        </div>

        <div className='entity-results-toolbar-actions'>
          <div
            className='entity-results-toolbar-table-actions'
            ref={setActionsPortalTarget}
          />
          {toolbarTrailingActions}
        </div>
      </div>

      {ownershipSettingLoading ? (
        <div
          className='entity-results-disabled-notice is-loading'
          role='status'
        >
          <div className='entity-results-disabled-notice-icon'>
            <SettingsOutlinedIcon aria-hidden='true' sx={{ fontSize: 18 }} />
          </div>
          <div className='entity-results-disabled-notice-copy'>
            <strong>Checking Beneficial Ownership settings</strong>
            <p id='ownership-results-loading-copy'>
              All Matches remains available while the database setting loads.
            </p>
          </div>
        </div>
      ) : !ownershipEnabled ? (
        <div className='entity-results-disabled-notice' role='status'>
          <div className='entity-results-disabled-notice-icon'>
            <LockOutlinedIcon aria-hidden='true' sx={{ fontSize: 18 }} />
          </div>
          <div className='entity-results-disabled-notice-copy'>
            <strong>Beneficial Ownership is disabled</strong>
            <p id='ownership-results-disabled-copy'>
              Ownership screening results are unavailable for this database.
              Enable Beneficial Ownership in Database Settings to view them.
            </p>
          </div>
          {onOpenSettings && (
            <button type='button' onClick={onOpenSettings}>
              <SettingsOutlinedIcon aria-hidden='true' sx={{ fontSize: 17 }} />
              Open Database Settings
            </button>
          )}
        </div>
      ) : null}

      {ownershipControlsArePageScoped && (
        <p
          id='ownership-results-page-scope'
          className='entity-results-page-scope-note'
        >
          Search and column sorting apply to the current page only.
        </p>
      )}

      {effectiveSource === 'all' ? (
        renderAllMatches(
          activeSearchQuery,
          true,
          reportAllCount,
          actionsPortalTarget,
        )
      ) : (
        <EntityScreeningResultsTable
          results={emptyEntityResults}
          ownershipResults={ownershipResults}
          page={ownershipPage}
          setPage={setOwnershipPage}
          handleSubmit={() => undefined}
          searchTerm={entityName}
          isStatic={true}
          serverPaginated={isServerPaginated}
          forcedTab='Ownership'
          hideToolbar={true}
          externalSearchQuery={activeSearchQuery}
          onResultCountChange={reportOwnershipCount}
        />
      )}
    </div>
  );
};

export default BiteDBEntityScreeningResults;
